import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { bookPagePath, booksInSeries, mediumWords, seriesPath, type SeriesEntry } from "../config/site";
import { loadBookHome, type BookHome } from "../data/load-book-home";

interface SeriesShelfProps {
  series: SeriesEntry;
  /** Shown over the covers, unless the page already names the series. */
  heading?: boolean;
}

/** One series' books, in order. */
export function SeriesShelf({ series, heading = true }: SeriesShelfProps) {
  const [missing, setMissing] = useState<ReadonlySet<string>>(new Set());
  // A book still being made has no data yet, so it gets no empty card.
  const books = booksInSeries(series.id).filter((book) => !missing.has(book.id));
  const [homes, setHomes] = useState<ReadonlyMap<string, BookHome>>(new Map());

  useEffect(() => {
    for (const book of booksInSeries(series.id)) {
      void loadBookHome(book.id).then((home) => {
        setHomes((current) => new Map(current).set(book.id, home));
      }).catch(() => {
        setMissing((current) => new Set(current).add(book.id));
      });
    }
  }, [series.id]);

  if (books.length === 0) {
    return null;
  }
  return (
    <section className="landing-series" aria-label={series.title}>
      {heading ? (
        <h3 className="landing-series__title">
          <Link to={seriesPath(series)}>{series.title}</Link>
        </h3>
      ) : null}
      <ul className="landing-books__list">
        {books.map((book) => {
          const home = homes.get(book.id);
          return (
            <li key={book.id}>
              <Link className="landing-book" to={bookPagePath(book)}>
                {home?.coverUrl === undefined
                  ? <span className="landing-book__cover" aria-hidden="true" />
                  : <img className="landing-book__cover" src={home.coverUrl} alt="" />}
                <span className="landing-book__title">{book.title}</span>
                {home === undefined ? null : (
                  <span className="landing-book__hint">
                    {home.summary.chapters.length} {mediumWords(book).part}
                    {home.summary.chapters.length === 1 ? "" : "s"}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
